"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRightCircle } from "central-icons/IconArrowRightCircle";
import { AnimatePresence, m } from "motion/react";
import { useState } from "react";
import DemoCheckbox from "@/components/preview/demo-checkbox";
import DemoFooter from "@/components/preview/demo-footer";
import DemoPanel from "@/components/preview/demo-panel";
import DemoSplit from "@/components/preview/demo-split";
import DemoWrapper from "@/components/preview/demo-wrapper";
import ResultLabel from "@/components/preview/result-label";
import { SPRING_FAST } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { SLIDE_HEIGHT } from "./slide-height";
const ALIGNMENT_MODES = [{
  label: "Geometric alignment",
  optical: false
}, {
  label: "Optical alignment",
  optical: true
}];
function AlignedButton(t0) {
  const $ = _c(25);
  const {
    debugMode,
    isOpticallyAligned
  } = t0;
  const rightPadding = isOpticallyAligned ? 20 : 24;
  let t1;
  if ($[0] !== debugMode || $[1] !== rightPadding) {
    t1 = debugMode ? <m.div animate={{
      filter: "blur(0px)",
      opacity: 1,
      scale: 1
    }} className="pointer-events-none absolute top-1/2 -right-16 flex -translate-y-1/2 select-none items-center justify-center rounded-full border border-sky-500 bg-sky-500/15 px-2 font-medium text-sky-500 text-sm" exit={{
      filter: "blur(4px)",
      opacity: 0,
      scale: 0.5
    }} initial={{
      filter: "blur(4px)",
      opacity: 0,
      scale: 0.5
    }} key="right-padding-value" transition={SPRING_FAST}>{rightPadding}px</m.div> : null;
    $[0] = debugMode;
    $[1] = rightPadding;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== debugMode) {
    t2 = debugMode ? <m.div animate={{
      filter: "blur(0px)",
      opacity: 1,
      scale: 1
    }} className="pointer-events-none absolute top-1/2 -left-16 flex -translate-y-1/2 select-none items-center justify-center rounded-full border border-sky-500 bg-sky-500/15 px-2 font-medium text-sky-500 text-sm" exit={{
      filter: "blur(4px)",
      opacity: 0,
      scale: 0.5
    }} initial={{
      filter: "blur(4px)",
      opacity: 0,
      scale: 0.5
    }} key="left-padding-value" transition={SPRING_FAST}>{24}px</m.div> : null;
    $[3] = debugMode;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <AnimatePresence initial={false} mode="popLayout">{t1}{t2}</AnimatePresence>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isOpticallyAligned ? "pr-5 pl-6" : "px-6";
  let t5;
  if ($[8] !== t4) {
    t5 = cn("relative flex h-12 select-none items-center justify-center gap-1.5 overflow-hidden rounded-full bg-preview-bg font-medium text-lg shadow-custom will-change-transform", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== debugMode) {
    t6 = debugMode ? <m.div animate={{
      opacity: 1
    }} className="pointer-events-none absolute inset-y-0 left-0 select-none bg-sky-500/30" exit={{
      opacity: 0
    }} initial={{
      opacity: 0
    }} key="left-padding" style={{
      width: "24px"
    }} transition={SPRING_FAST} /> : null;
    $[10] = debugMode;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== debugMode || $[13] !== rightPadding) {
    t7 = debugMode ? <m.div animate={{
      opacity: 1
    }} className="pointer-events-none absolute inset-y-0 right-0 select-none bg-sky-500/30" exit={{
      opacity: 0
    }} initial={{
      opacity: 0
    }} key="right-padding" style={{
      width: `${rightPadding}px`
    }} transition={SPRING_FAST} /> : null;
    $[12] = debugMode;
    $[13] = rightPadding;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <AnimatePresence initial={false} mode="popLayout">{t6}{t7}</AnimatePresence>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconArrowRightCircle className="size-5.5 text-gray-1100" />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t8) {
    t10 = <m.button className={t5} initial={false}>{t8}Button{t9}</m.button>;
    $[19] = t5;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t3) {
    t11 = <div className="relative flex items-center justify-center">{t3}{t10}</div>;
    $[22] = t10;
    $[23] = t3;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
export default function OpticallyAlignedButton() {
  const $ = _c(9);
  const [debugMode, setDebugMode] = useState(false);
  let t0;
  if ($[0] !== debugMode) {
    t0 = ALIGNMENT_MODES.map(mode => <DemoPanel footer={<ResultLabel bad={!mode.optical} className="whitespace-nowrap font-berkeley-mono" size="sm">{mode.label}</ResultLabel>} footerPosition="top" key={mode.label}><AlignedButton debugMode={debugMode} isOpticallyAligned={mode.optical} /></DemoPanel>);
    $[0] = debugMode;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <DemoSplit className="min-h-0 grow" stackBelow="sm">{t0}</DemoSplit>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== debugMode) {
    t2 = <DemoFooter><DemoCheckbox checked={debugMode} label="Show Padding" onCheckedChange={setDebugMode} /></DemoFooter>;
    $[4] = debugMode;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <DemoWrapper height={SLIDE_HEIGHT}>{t1}{t2}</DemoWrapper>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}